import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { I18nPluralPipe, TitleCasePipe } from '@angular/common';
import {
  TuiAlertModule,
  TuiDialogModule,
  TuiNotification,
  TuiRootModule,
  TUI_ANIMATIONS_DURATION,
  TUI_DIALOGS_CLOSE,
  TUI_ICONS_PATH,
  TUI_NOTIFICATION_DEFAULT_OPTIONS,
  TUI_NOTIFICATION_OPTIONS,
  TUI_SANITIZER
} from '@taiga-ui/core';
import { NgDompurifySanitizer } from '@tinkoff/ng-dompurify';
import { CUSTOM_DIALOG_PROVIDER } from './shared/components/dialog/dialog.component';
import {
  TUI_INPUT_COUNT_DEFAULT_OPTIONS,
  TUI_INPUT_COUNT_OPTIONS,
  TUI_INPUT_PASSWORD_DEFAULT_OPTIONS,
  TUI_INPUT_PASSWORD_OPTIONS,
  TUI_TOGGLE_DEFAULT_OPTIONS,
  TUI_TOGGLE_OPTIONS,
  TUI_VALIDATION_ERRORS
} from '@taiga-ui/kit';
import { iconsPath } from './icons';
import { environment } from '@fb-tool-front/environments/environment';
import { AuthService } from './core/services/auth.service';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
  TranslateService
} from '@ngx-translate/core';
import { LocaleService } from './core/services/locale.service';
import {
  MESSAGE_FORMAT_CONFIG,
  TranslateMessageFormatCompiler
} from 'ngx-translate-messageformat-compiler';
import { TUI_ENGLISH_LANGUAGE, TUI_LANGUAGE, TUI_RUSSIAN_LANGUAGE } from '@taiga-ui/i18n';
import { map } from 'rxjs/operators';
import { marker as extract } from '@biesbjerg/ngx-translate-extract-marker';
import { PixelModule } from 'ngx-pixel';
import { AngularCallTouchCounterModule } from 'angular-calltouch-counter';
import { GlobalErrorHandler } from './core/error-handlers/error.handler';
import { EventPluginsModule } from '@tinkoff/ng-event-plugins';
import { TUI_EDITOR_EXTENSIONS } from '@taiga-ui/addon-editor';

export const HttpLoaderFactory = (http: HttpClient) => {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    EventPluginsModule,
    AppRoutingModule,
    LayoutModule,
    SharedModule,
    CoreModule,
    TuiRootModule,
    TuiAlertModule,
    TuiDialogModule,
    AngularCallTouchCounterModule.forRoot({
      enabled: environment.production,
      counterId: environment.callTouchCounterId
    }),
    PixelModule.forRoot({ enabled: environment.production, pixelId: environment.facebookPixelId }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    })
  ],
  providers: [
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.locale
    },
    {
      provide: TUI_SANITIZER,
      useClass: NgDompurifySanitizer
    },
    {
      provide: TUI_ICONS_PATH,
      useValue: iconsPath
    },
    {
      provide: TUI_DIALOGS_CLOSE,
      deps: [AuthService],
      useFactory: ({ redirectToVendor$ }) => redirectToVendor$
    },
    {
      provide: TUI_ANIMATIONS_DURATION,
      useValue: 250
    },
    CUSTOM_DIALOG_PROVIDER,
    { provide: 'googleTagManagerId', useValue: environment.gtmId || 'NOT_INITIALIZED' },
    {
      provide: TUI_VALIDATION_ERRORS,
      useFactory: (translateService: TranslateService) => ({
        itemsSelected: (context: { maxItemsLength: number; currentItemsLength: number }) => {
          return `${translateService.instant(extract('Items selected'))}: ${
            context.currentItemsLength
          } / ${context.maxItemsLength}`;
        },
        audioDurationValidator: (context: { requiredValue: number }) => {
          return `${translateService.instant(
            extract('Wrong audio duration')
          )}. ${translateService.instant(extract('Minimum is'))} ${
            context.requiredValue
          }  ${translateService.instant(extract('seconds'))}`;
        },
        currentProductReplacement: `${translateService.instant(
          extract("You can't select current product as product replacement")
        )}`
      }),
      deps: [TranslateService]
    },
    {
      provide: 'virtual-scroller-default-options',
      useValue: {
        modifyOverflowStyleOfParentScroll: false
      }
    },
    {
      provide: TUI_INPUT_PASSWORD_OPTIONS,
      useValue: {
        ...TUI_INPUT_PASSWORD_DEFAULT_OPTIONS,
        icons: {
          hide: 'tuiIconEyeOff',
          show: 'tuiIconEye'
        }
      }
    },
    {
      provide: TUI_TOGGLE_OPTIONS,
      useValue: {
        ...TUI_TOGGLE_DEFAULT_OPTIONS,
        showIcons: true,
        size: 'l'
      }
    },
    {
      provide: TUI_INPUT_COUNT_OPTIONS,
      useValue: {
        ...TUI_INPUT_COUNT_DEFAULT_OPTIONS,
        icons: {
          up: 'tuiIconChevronUp',
          down: 'tuiIconChevronDown'
        }
      }
    },
    TitleCasePipe,
    I18nPluralPipe,
    {
      provide: TUI_NOTIFICATION_OPTIONS,
      useFactory: () => ({
        ...TUI_NOTIFICATION_DEFAULT_OPTIONS,
        autoClose: status => (status === TuiNotification.Error ? false : 7000)
      })
    },
    HttpClient,
    {
      provide: MESSAGE_FORMAT_CONFIG,
      useValue: {
        locales: ['en', 'ru']
      }
    },
    {
      provide: TUI_LANGUAGE,
      useFactory: (localeService: LocaleService) => {
        return localeService.locale$.pipe(
          map((locale: string) => (locale === 'ru' ? TUI_RUSSIAN_LANGUAGE : TUI_ENGLISH_LANGUAGE))
        );
      },
      deps: [LocaleService]
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: TUI_EDITOR_EXTENSIONS,
      useValue: [
        /* eslint-disable @typescript-eslint/naming-convention */
        import('@taiga-ui/addon-editor/extensions/starter-kit').then(
          ({ StarterKit }) => StarterKit
        ),
        import('./videos/modals/generate-speech/tools/emphasis/emphasis.extension').then(
          ({ EmphasisExtension }) => EmphasisExtension
        ),
        import('./videos/modals/generate-speech/tools/pauses/pauses.extension').then(
          ({ PausesExtension }) => PausesExtension
        ),
        import('./videos/modals/generate-speech/tools/pitch/pitch.extension').then(
          ({ PitchExtension }) => PitchExtension
        ),
        import('./videos/modals/generate-speech/tools/say-as/say-as.extension').then(
          ({ SayAsExtension }) => SayAsExtension
        ),
        import('./videos/modals/generate-speech/tools/speed/speed.extension').then(
          ({ SpeedExtension }) => SpeedExtension
        ),
        import('./videos/modals/generate-speech/tools/voice-effect/voice-effect.extension').then(
          ({ VoiceEffectExtension }) => VoiceEffectExtension
        ),
        import('./videos/modals/generate-speech/tools/volume/volume.extension').then(
          ({ VolumeExtension }) => VolumeExtension
        )
        /* eslint-enable @typescript-eslint/naming-convention */
      ]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
